import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { X, MapPin, Phone, Wrench, CheckCircle, Calendar, FileText, User, Trash2, Edit2 } from 'lucide-react';

const SERVICE_TYPE_LABELS = { service: 'Service Call', repair: 'Repair', warranty: 'Warranty' };
const SERVICE_TYPE_COLORS = {
  service:  'bg-orange-100 text-orange-700 border-orange-200',
  repair:   'bg-red-100 text-red-700 border-red-200',
  warranty: 'bg-blue-100 text-blue-700 border-blue-200',
};
const STATUS_COLORS = {
  pending:   'bg-yellow-100 text-yellow-700',
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

function formatDateFull(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  const weekdays = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const dt = new Date(y, m - 1, d);
  const day = dt.getDate();
  const suffix = [11,12,13].includes(day) ? 'th' : day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th';
  return `${weekdays[dt.getDay()]}, ${months[dt.getMonth()]} ${day}${suffix}`;
}

export default function ServiceDetailDrawer({ serviceId, onClose, onChanged, onEdit, userRole }) {
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!serviceId) return;
    setLoading(true);
    api.get(`/service/${serviceId}`).then(setSvc).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [serviceId]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const markComplete = async () => {
    await api.patch(`/service/${serviceId}/status`, { status: 'completed' });
    onChanged?.();
    onClose();
  };

  const cancelJob = async () => {
    if (!window.confirm('Cancel this service job?')) return;
    await api.patch(`/service/${serviceId}/status`, { status: 'cancelled' });
    onChanged?.();
    onClose();
  };

  const unschedule = async () => {
    if (!window.confirm('Remove the scheduled date? The service will go back to pending.')) return;
    // Fetch, strip date, update
    const current = await api.get(`/service/${serviceId}`);
    await api.put(`/service/${serviceId}`, {
      ...current,
      scheduled_date: null,
      scheduled_time_start: null,
      status: 'pending',
    });
    onChanged?.();
    onClose();
  };

  const deleteJob = async () => {
    if (!window.confirm(`Delete service job for ${svc.display_name || svc.contact_name}? This cannot be undone.`)) return;
    await api.delete(`/service/${serviceId}`);
    onChanged?.();
    onClose();
  };

  if (!serviceId) return null;

  const address = svc?.display_address || svc?.address;
  const city    = svc?.display_city    || svc?.city;
  const phone   = svc?.display_phone   || svc?.phone;
  const name    = svc?.display_name    || svc?.contact_name;
  const mapUrl  = address || city ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([address, city, 'ON'].filter(Boolean).join(', '))}` : null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} className="fixed inset-0 bg-black/40 z-[9998]" />

      {/* Drawer */}
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-white shadow-2xl z-[9999] flex flex-col overflow-hidden">
        {loading || !svc ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Loading…</div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-4 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Wrench className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-wide font-semibold opacity-90">Service Job</span>
                </div>
                <h2 className="text-xl font-bold">{name}</h2>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${SERVICE_TYPE_COLORS[svc.service_type]}`}>
                    {SERVICE_TYPE_LABELS[svc.service_type]}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[svc.status]}`}>
                    {svc.status}
                  </span>
                </div>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto px-5 py-4 space-y-5">
              {/* Schedule */}
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-1">Scheduled</p>
                    {svc.scheduled_date ? (
                      <>
                        <p className="font-semibold text-gray-800">{formatDateFull(svc.scheduled_date)}</p>
                        {svc.scheduled_time_start && (
                          <p className="text-sm text-gray-500 mt-0.5">Start time: {svc.scheduled_time_start}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-yellow-700 font-medium">Not scheduled yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address */}
              {(address || city) && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Location</p>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      {address && <p className="text-sm text-gray-800">{address}</p>}
                      {city && <p className="text-sm text-gray-600">{city}{svc.province ? `, ${svc.province}` : ''}</p>}
                      {mapUrl && (
                        <a href={mapUrl} target="_blank" rel="noreferrer"
                          className="text-xs text-orange-600 hover:underline inline-block mt-1">
                          Open in Google Maps ↗
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Phone */}
              {phone && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Contact</p>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400 shrink-0" />
                    <a href={`tel:${phone}`} className="text-sm text-orange-600 hover:underline">{phone}</a>
                  </div>
                </div>
              )}

              {/* Description */}
              {svc.description && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Issue / Description</p>
                  <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {svc.description}
                  </div>
                </div>
              )}

              {/* Crew notes */}
              {svc.crew_notes && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Crew Notes</p>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm text-amber-800">
                    {svc.crew_notes}
                  </div>
                </div>
              )}

              {/* Internal notes */}
              {svc.notes && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Internal Notes</p>
                  <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {svc.notes}
                  </div>
                </div>
              )}

              {/* Linked sale */}
              {svc.sale_id && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Linked Sale</p>
                  <a href={`/sales/${svc.sale_id}`} className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline">
                    <User className="w-4 h-4" /> View original sale #{svc.sale_id} →
                  </a>
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="border-t bg-gray-50 px-5 py-3 space-y-2">
              {svc.status === 'scheduled' && (
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={markComplete}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium">
                    <CheckCircle className="w-4 h-4" /> Mark Complete
                  </button>
                  <button onClick={() => { onEdit?.(svc); onClose(); }}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white border text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                    <Edit2 className="w-4 h-4" /> Reschedule / Edit
                  </button>
                </div>
              )}
              {svc.status === 'pending' && (
                <button onClick={() => { onEdit?.(svc); onClose(); }}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium">
                  <Calendar className="w-4 h-4" /> Schedule This Service
                </button>
              )}
              {svc.status === 'completed' && (
                <button onClick={() => { onEdit?.(svc); onClose(); }}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-white border text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                  <Edit2 className="w-4 h-4" /> Edit Details
                </button>
              )}
              <div className="flex gap-2">
                {svc.status === 'scheduled' && (
                  <button onClick={unschedule}
                    className="flex-1 text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5">
                    Unschedule
                  </button>
                )}
                {svc.status !== 'cancelled' && svc.status !== 'completed' && (
                  <button onClick={cancelJob}
                    className="flex-1 text-xs text-red-500 hover:text-red-700 px-3 py-1.5">
                    Cancel Job
                  </button>
                )}
                {userRole === 'admin' && (
                  <button onClick={deleteJob}
                    className="flex items-center justify-center gap-1 text-xs text-red-400 hover:text-red-600 px-3 py-1.5">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
